import { useLayoutEffect, useState, type RefObject } from 'react';

/**
 * @param textRef - The reference attach to the text ellipsis component
 * @param itemName - The item name without cropping.
 * @param width - the width of the container
 * @param height - the height of the container
 * @returns true or false depending whether name is truncated or not.
 */
export const useTruncationDetector = (
  textRef: RefObject<HTMLSpanElement | null>,
  itemName: string,
  width?: number,
  height?: number,
): boolean => {
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    if (textRef.current) {
      const observer = new MutationObserver((mutations) => {
        const [textMutation] = mutations ?? [];

        if (textMutation && textMutation.target instanceof HTMLSpanElement) {
          const text = textMutation.target.innerText;
          setIsTruncated(text.length < itemName.length);
        }
      });

      observer.observe(textRef.current, {
        characterData: true,
        childList: true,
        subtree: true,
      });

      const firstChild = textRef.current.firstChild;

      if (
        firstChild &&
        firstChild instanceof HTMLSpanElement &&
        firstChild.innerText
      ) {
        setIsTruncated(firstChild.innerText.length < itemName.length);
      }

      return () => observer.disconnect();
    }
  }, [itemName, textRef, width, height]);

  return isTruncated;
};
